import { styled, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { compareDesc, parseISO } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileDetailsEditModal } from '../EditModal/FileDetailsEditModal';
import { ProductFileData } from '../mapSrcToProductFiles';
import { UploadedFilesRow } from './UploadedFilesRow';

type UploadedFilesProps = Readonly<{
  uploadedFiles: ProductFileData[] | undefined;
}>;

export function UploadedFiles({ uploadedFiles }: UploadedFilesProps) {
  const [openEditDetailsModal, setOpenEditDetailsModal] = useState(false);
  const [fileToEdit, setFileToEdit] = useState<ProductFileData | undefined>();

  const { t } = useTranslation('uploads');

  const openModal = (file: ProductFileData) => {
    setFileToEdit(file);
    setOpenEditDetailsModal(true);
  };

  /* istanbul ignore next */
  const closeModal = () => {
    setFileToEdit(undefined);
    setOpenEditDetailsModal(false);
  };

  if (!uploadedFiles?.length) {
    return null;
  }

  const sortedUploadedFiles = [...uploadedFiles].sort((a, b) => compareCreated(a, b));

  const isDetailsModalOpen = Boolean(openEditDetailsModal && fileToEdit); // Make sure file is not `undefined`
  return (
    <div>
      {fileToEdit ? (
        <FileDetailsEditModal isOpen={isDetailsModalOpen} onClose={closeModal} productFile={fileToEdit} />
      ) : null}
      <Table sx={{ minWidth: 650, overflow: 'auto' }} aria-label="simple table">
        <colgroup>
          <col style={{ width: '10%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
        </colgroup>
        <TableHead>
          <HeaderRow>
            <HeaderCell>{t('moreInfo')}</HeaderCell>
            <HeaderCell>{t('name')}</HeaderCell>
            <HeaderCell>{t('assignToProduct')}</HeaderCell>
            <HeaderCell>{t('uploadedOn')}</HeaderCell>
            <HeaderCell>{t('size')}</HeaderCell>
            <HeaderCell />
          </HeaderRow>
        </TableHead>
        <TableBody>
          {sortedUploadedFiles.map((productFile) => (
            <UploadedFilesRow key={productFile.name} productFile={productFile} openModal={openModal} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function compareCreated(a: ProductFileData, b: ProductFileData) {
  /* istanbul ignore next */
  if (!a.createdAt || !b.createdAt) return 0;
  const getDate = (fileData: ProductFileData) => {
    const fileDate = fileData?.updatedAt ?? fileData?.createdAt ?? /* istanbul ignore next */ '';
    return parseISO(String(fileDate));
  };

  return compareDesc(getDate(a), getDate(b));
}

const HeaderCell = styled(TableCell)`
  font-weight: bold;
`;

const HeaderRow = styled(TableRow)`
  th {
    border-bottom: solid 1px ${({ theme }) => theme.palette.grey[500]};
  }
`;
